import {
  ConfirmationNumberRounded,
  ContentCopyRounded,
  ListAlt,
  Lock,
  Pause,
  ThumbsUpDown,
} from '@mui/icons-material';
import { Box, Theme, useMediaQuery } from '@mui/material';
import { useState } from 'react';

import { Button, ButtonVariants, Label, LocalModal } from '@/components';
import { ComplianceTypes } from '@/constants';
import {
  BookmarkBorderIcon,
  BookmarkIcon,
  PlayArrowFilledIcon,
  PlayArrowOutlinedIcon,
  ShareIcon,
} from '@/icons';
import { ComplianceType, EpisodeStatus } from '@/types';

import { EpisodeSocialLinks } from './EpisodeSocialLinks';

const PrimaryButtons = {
  PLAY: 'play',
  REDEEM: 'redeem',
  ASSESSMENT: 'assessment',
  FEEDBACK: 'feedback',
};

export type EpisodeControlButtonsComponentProps = {
  compliance: ComplianceType;
  isUserAnonymous: boolean;
  isUserInternal: boolean;
  isEpisodeRedeemRequired: boolean;
  isEpisodePremium: boolean;
  episodeId: string;
  episodeStatus?: EpisodeStatus;
  newFeatureAlert?: { isVisible: boolean; dismiss: () => void };
  episodeDeepLink: string;
  onRedeem: () => void;
  onPlayNow: () => void;
  isBookmarked: boolean;
  isBookmarking: boolean;
  onToggleBookmark: () => void;
  onTrailer: () => void;
  onTakeAssessment: () => void;
  onGiveFeedback: () => void;
  onShare: () => void;
  onCopyLink: () => void;
  isPlaying?: boolean;
  isPreviewing?: boolean;
};

export const EpisodeControlButtons = ({
  compliance,
  isUserAnonymous,
  isUserInternal,
  isEpisodeRedeemRequired,
  isEpisodePremium,
  episodeId,
  episodeStatus = 'none',
  newFeatureAlert,
  episodeDeepLink,
  onRedeem,
  onPlayNow,
  isBookmarked,
  onToggleBookmark,
  onTrailer,
  onTakeAssessment,
  onGiveFeedback,
  onShare,
  onCopyLink,
  isPlaying,
  isPreviewing,
}: EpisodeControlButtonsComponentProps) => {
  const isMdDown = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );

  const [isSocialLinksModalOpen, setIsSocialLinksModalOpen] = useState(false);

  const showAssessmentButton =
    compliance === ComplianceTypes.CPE && !isUserAnonymous;
  const showFeedbackButton =
    episodeStatus === 'verified' && compliance === ComplianceTypes.CPE;

  const primaryButton = getPrimaryButton({
    isEpisodeRedeemRequired,
    episodeStatus,
    showFeedbackButton,
  });

  const playButtonText = getPlayButtonText({
    episodeStatus,
    isMdDown,
  });

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          columnGap: 3,
          rowGap: 1,
          flexWrap: 'wrap',
        }}>
        <Button
          variant={ButtonVariants.PLAIN}
          fullWidth={false}
          skinny={true}
          onClick={onToggleBookmark}
          label="Bookmark"
          aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          startIcon={isBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
        />
        {!isUserInternal ? (
          <Button
            variant={ButtonVariants.PLAIN}
            fullWidth={false}
            skinny={true}
            onClick={() => {
              newFeatureAlert?.dismiss();
              onShare();
            }}
            label="Share"
            aria-label="Share"
            startIcon={<ShareIcon />}
            endIcon={newFeatureAlert?.isVisible && <Label text="New" />}
          />
        ) : (
          <>
            <Button
              variant={ButtonVariants.PLAIN}
              fullWidth={false}
              skinny={true}
              onClick={onCopyLink}
              label="Copy Episode Link"
              aria-label="Copy Episode Link"
              startIcon={<ContentCopyRounded />}
            />
            <Button
              variant={ButtonVariants.PLAIN}
              fullWidth={false}
              skinny={true}
              onClick={() => setIsSocialLinksModalOpen(true)}
              label="Generate Social Links"
              aria-label="Generate Social Links"
              startIcon={<ShareIcon />}
            />
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <PrimaryButton
            isEpisodePremium={isEpisodePremium}
            isEpisodeRedeemRequired={isEpisodeRedeemRequired}
            primaryButton={primaryButton}
            playButtonText={playButtonText}
            onPlayNow={onPlayNow}
            onRedeem={onRedeem}
            isPlaying={isPlaying}
          />
          <PreviewButton onTrailer={onTrailer} isPreviewing={isPreviewing} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {showAssessmentButton && (
            <Button
              variant={
                primaryButton === PrimaryButtons.ASSESSMENT
                  ? ButtonVariants.PRIMARY
                  : ButtonVariants.SECONDARY
              }
              label={
                episodeStatus === 'verified'
                  ? 'View Assessment'
                  : 'Take Assessment'
              }
              fullWidth={false}
              skinny={false}
              onClick={onTakeAssessment}
              startIcon={<ListAlt />}
            />
          )}
          {showFeedbackButton && (
            <Button
              variant={
                primaryButton === PrimaryButtons.FEEDBACK
                  ? ButtonVariants.PRIMARY
                  : ButtonVariants.TERTIARY
              }
              label="Give Feedback"
              fullWidth={false}
              skinny={false}
              onClick={onGiveFeedback}
              startIcon={<ThumbsUpDown />}
            />
          )}
        </Box>
      </Box>
      <LocalModal
        name="Episode Social Links Modal"
        open={isSocialLinksModalOpen}
        onClose={() => setIsSocialLinksModalOpen(false)}>
        <EpisodeSocialLinks
          episodeDeepLink={episodeDeepLink}
          episodeId={episodeId}
          onClose={() => setIsSocialLinksModalOpen(false)}
        />
      </LocalModal>
    </Box>
  );
};

const PreviewButton = ({
  onTrailer,
  isPreviewing,
}: {
  onTrailer: () => void;
  isPreviewing?: boolean;
}) => {
  return (
    <Box sx={{ display: { xs: 'none', md: 'inline' } }}>
      <Button
        variant={ButtonVariants.SECONDARY}
        label="Preview"
        fullWidth={false}
        skinny={false}
        onClick={onTrailer}
        startIcon={
          isPreviewing ? (
            <Pause sx={{ width: 20, height: 20 }} />
          ) : (
            <PlayArrowOutlinedIcon sx={{ width: 20, height: 20 }} />
          )
        }
      />
    </Box>
  );
};

const PrimaryButton = ({
  isEpisodePremium,
  isEpisodeRedeemRequired,
  primaryButton,
  playButtonText,
  onPlayNow,
  onRedeem,
  isPlaying,
}: {
  isEpisodePremium: boolean;
  isEpisodeRedeemRequired: boolean;
  primaryButton: string;
  playButtonText: string;
  onPlayNow: () => void;
  onRedeem: () => void;
  isPlaying?: boolean;
}) => {
  if (isEpisodePremium) {
    return (
      <Button
        variant={ButtonVariants.PREMIUM}
        label="Premium"
        fullWidth={false}
        skinny={false}
        onClick={onPlayNow}
        startIcon={<Lock />}
      />
    );
  }

  if (isEpisodeRedeemRequired) {
    return (
      <Button
        variant={
          primaryButton === PrimaryButtons.REDEEM
            ? ButtonVariants.PRIMARY
            : ButtonVariants.SECONDARY
        }
        label="Redeem"
        fullWidth={false}
        skinny={false}
        onClick={onRedeem}
        startIcon={<ConfirmationNumberRounded />}
      />
    );
  }

  return (
    <Button
      variant={
        primaryButton === PrimaryButtons.PLAY
          ? ButtonVariants.PRIMARY
          : ButtonVariants.SECONDARY
      }
      label={playButtonText}
      fullWidth={false}
      skinny={false}
      onClick={onPlayNow}
      startIcon={
        isPlaying ? (
          <Pause sx={{ width: 18, height: 18 }} />
        ) : (
          <PlayArrowFilledIcon sx={{ width: 18, height: 18 }} />
        )
      }
    />
  );
};

const getPrimaryButton = ({
  isEpisodeRedeemRequired,
  episodeStatus,
  showFeedbackButton,
}: {
  isEpisodeRedeemRequired: boolean;
  episodeStatus: EpisodeStatus;
  showFeedbackButton: boolean;
}) => {
  if (isEpisodeRedeemRequired) return PrimaryButtons.REDEEM;
  if (showFeedbackButton) return PrimaryButtons.FEEDBACK;
  if (episodeStatus === 'assessmentReady') return PrimaryButtons.ASSESSMENT;
  return PrimaryButtons.PLAY;
};

const getPlayButtonText = ({
  episodeStatus,
  isMdDown,
}: {
  episodeStatus: EpisodeStatus;
  isMdDown: boolean;
}) => {
  if (episodeStatus === 'verified' || episodeStatus === 'completed')
    return isMdDown ? 'Play' : 'Play Again';
  if (!episodeStatus || episodeStatus === 'none')
    return isMdDown ? 'Play' : 'Play Now';
  return 'Continue';
};
